import React, {
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import { VelavuAPI } from "velavu-js-api";
import { useProvideContentRefreshable } from "../../elements/refresh-context";

export interface AssetImageURLCache {
	getAssetImageURL(assetID: string): string | null | undefined;
	setAssetImageURL(assetID: string, url: string | null): void;
}

export const AssetImageURLCacheContext =
	React.createContext<AssetImageURLCache>({
		getAssetImageURL: () => null,
		setAssetImageURL: () => undefined,
	});

export interface AssetImageURLCacheProviderProps {
	children: React.ReactNode;
}

/**
 * A provider that caches asset profile image URLs
 */
export function AssetImageURLCacheProvider(
	props: AssetImageURLCacheProviderProps,
) {
	const [urlCache, setURLCache] = useState<Record<string, string | null>>({});
	const pendingRequests = useRef(new Set<string>()).current;

	const refreshContent = useCallback(() => {
		setURLCache({});
		return Promise.resolve();
	}, [setURLCache]);
	useProvideContentRefreshable(refreshContent);

	const getAssetImageURL = useCallback(
		(assetID: string) => {
			//Look up the URL in the cache
			if (assetID in urlCache) {
				return urlCache[assetID];
			}

			//Make a request to get the asset details
			if (!pendingRequests.has(assetID)) {
				pendingRequests.add(assetID);
				VelavuAPI.assets
					.getSpecificAsset(assetID)
					.then((asset) => {
						const resolvedURL = asset.profile_img_url ?? null;
						setURLCache((prev) => ({
							...prev,
							[assetID]: resolvedURL,
						}));
					})
					.catch((err) => {
						console.warn(
							`Error fetching asset profile URL for asset ${assetID}`,
							err,
						);
					})
					.finally(() => {
						pendingRequests.delete(assetID);
					});
			}
		},
		[urlCache, setURLCache, pendingRequests],
	);

	const setAssetImageURL = useCallback(
		(assetID: string, url: string | null) => {
			setURLCache((prev) => ({
				...prev,
				[assetID]: url,
			}));
		},
		[setURLCache],
	);

	const providerValue = useMemo(
		(): AssetImageURLCache => ({
			getAssetImageURL,
			setAssetImageURL,
		}),
		[getAssetImageURL, setAssetImageURL],
	);

	return (
		<AssetImageURLCacheContext.Provider value={providerValue}>
			{props.children}
		</AssetImageURLCacheContext.Provider>
	);
}

export function useAssetImageURLCache(): AssetImageURLCache {
	return useContext(AssetImageURLCacheContext);
}
